import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';

import Card, { CardContent, CardHeader, CardTitle } from '@/components/ui/Card';

const LAST_UPDATED = 'April 9, 2026';
const CONTACT_EMAIL = 'legal@voteplan.app';

function Section({ title, children }) {
  return (
    <section className="scroll-mt-24">
      <h2 className="font-serif text-2xl font-bold text-neutral-900">{title}</h2>
      <div className="mt-3 space-y-4 text-sm leading-7 text-neutral-700 md:text-base">
        {children}
      </div>
    </section>
  );
}

Section.propTypes = {
  title: PropTypes.string.isRequired,
  children: PropTypes.node.isRequired,
};

export default function TermsPage() {
  return (
    <div className="min-h-screen bg-neutral-50">
      <div className="mx-auto max-w-4xl px-4 py-10 sm:px-6 lg:px-8">
        <div className="mb-8">
          <Link
            to="/"
            className="text-sm font-medium text-primary-600 transition hover:text-primary-700"
          >
            ← Back to home
          </Link>
        </div>

        <Card className="border-neutral-200 shadow-sm">
          <CardHeader className="border-b border-neutral-200 bg-white">
            <p className="text-sm font-medium uppercase tracking-wide text-neutral-500">Legal</p>
            <CardTitle className="mt-2 font-serif text-3xl text-neutral-900 md:text-4xl">
              Terms of Service
            </CardTitle>
            <p className="mt-3 max-w-3xl text-sm leading-7 text-neutral-600 md:text-base">
              These Terms of Service govern your use of VotePlan, including the website,
              voter-information tools, account features, ballot and election information, reminder
              features, and any related mailer or delivery services we make available.
            </p>
            <p className="mt-3 text-sm text-neutral-500">Last updated: {LAST_UPDATED}</p>
          </CardHeader>

          <CardContent className="space-y-10 bg-white py-8">
            <Section title="1. Acceptance of these Terms">
              <p>
                By accessing or using VotePlan, you agree to these Terms of Service and our{' '}
                <Link to="/privacy" className="text-primary-600 hover:text-primary-700">
                  Privacy Policy
                </Link>
                . If you do not agree, do not use the service.
              </p>
              <p>
                You may use VotePlan only if you are legally capable of entering into a binding
                agreement. If you use VotePlan on behalf of an organization, you represent that you
                have authority to bind that organization to these terms.
              </p>
            </Section>

            <Section title="2. What VotePlan provides">
              <p>
                VotePlan is an informational civic platform. We may provide election dates,
                registration deadlines, voting-window schedules, polling-place information,
                ballot-related content, district information, reminders, and optional ballot-guide
                or mail-delivery features.
              </p>
              <p>
                Our service is designed to help users find and understand voting information, but it
                is not a government agency, election office, law firm, or emergency service. We do
                not guarantee that any election authority will accept, process, or honor actions you
                take based solely on information shown in the app.
              </p>
            </Section>

            <Section title="3. No legal advice; no government affiliation">
              <p>
                VotePlan provides general informational tools only. Nothing on the site is legal
                advice, an attorney-client communication, or an official statement from any election
                authority.
              </p>
              <p>
                Unless we explicitly say otherwise, VotePlan is independent from any federal, state,
                county, municipal, or other government office. Official deadlines, eligibility
                rules, polling-place assignments, and ballot procedures are set by the relevant
                election authorities and may change.
              </p>
            </Section>

            <Section title="4. Accuracy, timeliness, and source limitations">
              <p>
                We work to gather and present information from official and other authoritative
                sources, but election information can change quickly and sometimes without notice.
                Data may contain errors, omissions, delays, stale information, scraping errors,
                mapping errors, or formatting issues introduced by third-party systems.
              </p>
              <p>
                You are responsible for confirming critical information directly with the relevant
                election office before relying on it for time-sensitive decisions, including voter
                registration, absentee ballot requests, ballot return methods, polling-place
                locations, required identification, and voting hours.
              </p>
            </Section>

            <Section title="5. Accounts and account security">
              <p>
                Some features may require an account. You agree to provide accurate information and
                to keep your login credentials secure. You are responsible for activity that occurs
                under your account unless caused by our own unauthorized actions or security
                failure.
              </p>
              <p>
                You must promptly notify us if you believe your account has been accessed without
                authorization. We may suspend or disable accounts that appear compromised or that
                violate these Terms.
              </p>
            </Section>

            <Section title="6. User content and submissions">
              <p>
                If you upload, submit, or enter content such as addresses, saved preferences,
                correction requests, mailer details, or feedback, you retain your rights in that
                content. You grant us a non-exclusive, worldwide, royalty-free license to host,
                store, reproduce, modify, and use that content only as needed to operate, maintain,
                secure, improve, and provide the service.
              </p>
              <p>
                You are responsible for ensuring that you have the right to provide any submitted
                content and that it does not violate any law or third-party rights.
              </p>
            </Section>

            <Section title="7. Prohibited conduct">
              <p>You may not use VotePlan to:</p>
              <ul className="list-disc space-y-2 pl-6">
                <li>break the law or encourage others to do so;</li>
                <li>interfere with the service, security, or availability of our systems;</li>
                <li>
                  scrape, harvest, or bulk-download service content in violation of applicable law
                  or our published access rules;
                </li>
                <li>impersonate any person or entity or misrepresent your affiliation;</li>
                <li>submit false, fraudulent, or misleading information;</li>
                <li>reverse engineer, probe, or test vulnerabilities in a harmful way;</li>
                <li>use the service to spam, phish, stalk, harass, or distribute malware; or</li>
                <li>
                  use automated means to overload the platform or bypass rate limits,
                  authentication, or access controls.
                </li>
              </ul>
            </Section>

            <Section title="8. Third-party services and links">
              <p>
                VotePlan may link to or integrate with third-party services, including payment,
                geocoding, email, print-and-mail, mapping, analytics, and election-authority
                websites. We do not control third-party services and are not responsible for their
                availability, content, security, or practices.
              </p>
              <p>
                Your use of third-party services is governed by their own terms and privacy
                practices.
              </p>
            </Section>

            <Section title="9. Mailers, deliveries, and paid features">
              <p>
                If VotePlan offers paid mailers, ballot guides, premium features, or delivery
                services, you agree to provide accurate order and delivery information. Delivery
                timing may depend on third-party vendors, carriers, election calendars, holidays,
                weather, address accuracy, and print production windows.
              </p>
              <p>
                We do not guarantee that printed or mailed materials will arrive by a voter’s
                preferred date, registration deadline, ballot deadline, or election day unless we
                explicitly make that guarantee in writing. Users remain responsible for acting with
                sufficient lead time.
              </p>
              <p>
                Fees, billing terms, renewal terms, refunds, and cancellation rules for paid
                features may be presented at checkout or in a separate order flow and are
                incorporated into these Terms by reference.
              </p>
            </Section>

            <Section title="10. Text, email, and reminder communications">
              <p>
                If you opt in to reminders, email updates, or text messages, you consent to receive
                those communications at the contact information you provide, subject to applicable
                law. Message frequency may vary. Message and data rates may apply.
              </p>
              <p>
                You may unsubscribe or opt out where those features are offered, but service-related
                and transactional communications may still be sent when necessary to operate your
                account or complete a requested transaction.
              </p>
            </Section>

            <Section title="11. Intellectual property">
              <p>
                VotePlan and its original software, design, branding, text, and compiled content,
                excluding third-party and public-source material, are protected by intellectual
                property laws. Subject to these Terms, we grant you a limited, non-exclusive,
                revocable license to use the service for its intended personal or internal business
                purposes.
              </p>
              <p>
                You may not copy, sell, sublicense, modify, distribute, or create derivative works
                from the service except as allowed by law or with our prior written permission.
              </p>
            </Section>

            <Section title="12. Suspension and termination">
              <p>
                We may suspend or terminate access to all or part of the service at any time if we
                reasonably believe you have violated these Terms, created risk for other users or
                us, or if required by law. We may also discontinue features or the entire service.
              </p>
              <p>
                You may stop using the service at any time. Sections that by their nature should
                survive termination will survive, including ownership, disclaimers, limitations of
                liability, indemnity, and dispute-related provisions.
              </p>
            </Section>

            <Section title="13. Disclaimers">
              <p>
                THE SERVICE IS PROVIDED ON AN “AS IS” AND “AS AVAILABLE” BASIS TO THE MAXIMUM EXTENT
                PERMITTED BY LAW. TO THE MAXIMUM EXTENT PERMITTED BY LAW, VOTEPLAN DISCLAIMS ALL
                WARRANTIES, EXPRESS OR IMPLIED, INCLUDING IMPLIED WARRANTIES OF MERCHANTABILITY,
                FITNESS FOR A PARTICULAR PURPOSE, TITLE, NON-INFRINGEMENT, AND ANY WARRANTY THAT THE
                SERVICE WILL BE UNINTERRUPTED, ERROR-FREE, SECURE, OR ACCURATE.
              </p>
              <p>
                We do not warrant that the service will meet your needs, that election information
                will always be complete or current, or that any reminder, alert, message, or mailed
                item will be delivered or received at a particular time.
              </p>
            </Section>

            <Section title="14. Limitation of liability">
              <p>
                TO THE MAXIMUM EXTENT PERMITTED BY LAW, VOTEPLAN AND ITS AFFILIATES, OFFICERS,
                EMPLOYEES, CONTRACTORS, AND SERVICE PROVIDERS WILL NOT BE LIABLE FOR ANY INDIRECT,
                INCIDENTAL, SPECIAL, CONSEQUENTIAL, EXEMPLARY, OR PUNITIVE DAMAGES, OR FOR ANY LOSS
                OF PROFITS, REVENUE, DATA, GOODWILL, OR BUSINESS OPPORTUNITIES, ARISING OUT OF OR
                RELATING TO YOUR USE OF THE SERVICE.
              </p>
              <p>
                TO THE MAXIMUM EXTENT PERMITTED BY LAW, OUR TOTAL LIABILITY FOR ALL CLAIMS ARISING
                OUT OF OR RELATING TO THE SERVICE WILL NOT EXCEED THE GREATER OF (A) THE AMOUNT YOU
                PAID US FOR THE SPECIFIC SERVICE GIVING RISE TO THE CLAIM IN THE 12 MONTHS BEFORE
                THE EVENT GIVING RISE TO LIABILITY, OR (B) FIFTY U.S. DOLLARS (US $50).
              </p>
              <p>
                Some jurisdictions do not allow certain limitations, so some of the above may not
                apply to you.
              </p>
            </Section>

            <Section title="15. Indemnification">
              <p>
                To the maximum extent permitted by law, you agree to defend, indemnify, and hold
                harmless VotePlan and its affiliates, officers, employees, contractors, and service
                providers from and against claims, liabilities, damages, judgments, losses, and
                expenses, including reasonable attorneys’ fees, arising out of or related to your
                misuse of the service, your content, or your violation of these Terms or applicable
                law.
              </p>
            </Section>

            <Section title="16. Changes to the service or these Terms">
              <p>
                We may modify the service or these Terms from time to time. When we make material
                changes, we may update the date at the top of this page and, where appropriate,
                provide additional notice. Your continued use of the service after the effective
                date of updated Terms constitutes acceptance of the revised Terms.
              </p>
            </Section>

            <Section title="17. Governing law and disputes">
              <p>
                These Terms are governed by the laws of the state identified in our business
                formation documents, without regard to conflict-of-law rules, except to the extent
                superseded by applicable federal law.
              </p>
              <p>
                Any dispute arising out of or relating to these Terms or the service will be
                resolved exclusively in the state or federal courts located in that state, and you
                consent to personal jurisdiction and venue there, unless applicable law requires a
                different forum.
              </p>
              <p>
                If your business entity or principal place of business is not yet finalized, update
                this section before production launch with the correct governing state, venue, and
                any required consumer-protection carveouts.
              </p>
            </Section>

            <Section title="18. Miscellaneous">
              <p>
                If any provision of these Terms is found unenforceable, the remaining provisions
                will remain in effect. Our failure to enforce any provision is not a waiver. These
                Terms, together with any incorporated terms and policies, form the entire agreement
                between you and VotePlan regarding the service.
              </p>
            </Section>

            <Section title="19. Contact">
              <p>
                For questions about these Terms, contact us at{' '}
                <a
                  href={`mailto:${CONTACT_EMAIL}`}
                  className="text-primary-600 hover:text-primary-700"
                >
                  {CONTACT_EMAIL}
                </a>
                .
              </p>
              <p>
                Before launch, replace the placeholder email and add your legal business name and
                mailing address if you want a fuller notice-contact section.
              </p>
            </Section>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
